import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'

// onEnter: chat mode. Enter calls it instead of starting a paragraph and
// Shift+Enter still breaks the line (StarterKit's hard break), like Discord.
// Only where there's a real keyboard: on touch, Enter stays a newline and the
// send button sends, which is what Discord's phone app does too.
// focusKey: focus the editor whenever it changes, without scrolling anything.
// onCaret: called with the text just before the caret whenever it moves or the
// text changes, which is how the @mention picker knows what's being typed.
// onKeyDown: gets first look at every key and returns true to swallow it, so
// an open picker can own the arrows and Enter.
// onEditor: hands the editor up once it exists, for inserting text.
const TipTapPrivatePost = ({ html, updateHtml, idx, onEnter, focusKey, onCaret, onKeyDown, onEditor, placeholder = 'Start typing here...' }) => {
  const onEnterRef = useRef(onEnter)
  onEnterRef.current = onEnter
  const onCaretRef = useRef(onCaret)
  onCaretRef.current = onCaret
  const onKeyDownRef = useRef(onKeyDown)
  onKeyDownRef.current = onKeyDown

  const reportCaret = (editor) => {
    if (!onCaretRef.current) { return }
    const { from, empty } = editor.state.selection
    const before = empty ? editor.state.doc.textBetween(Math.max(0, from - 41), from, '\n', '\n') : ''
    onCaretRef.current(before, from)
  }

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false })
    ],
    onUpdate({ editor }) {
      updateHtml(editor.getHTML())
      reportCaret(editor)
    },
    onSelectionUpdate({ editor }) { reportCaret(editor) },
    onBlur() { onCaretRef.current?.('', 0) },
    editorProps: {
      handleKeyDown: (view, event) => {
        if (onKeyDownRef.current?.(event)) { event.preventDefault(); return true }
        if (!onEnterRef.current || event.key !== 'Enter' || event.shiftKey || event.isComposing) { return false }
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) { return false }
        event.preventDefault()
        onEnterRef.current()
        return true
      },
      handleDOMEvents: {
        keydown: (view, event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault()
            // preventDefault stops ProseMirror reaching handleKeyDown, so in
            // chat mode Cmd/Ctrl+Enter sends from here, touch or not.
            if (onEnterRef.current) { onEnterRef.current(); return true }
            return false
          }
        }
      },
      attributes: {
        class: 'p-2 text-sm focus:outline-none dark:text-gray-100'
      }
    },
    content: html
  })

  useEffect(() => {
    if (idx > 1) { editor?.commands.clearContent(true) }
  }, [idx])

  useEffect(() => { if (editor) onEditor?.(editor) }, [editor])

  useEffect(() => {
    if (focusKey === undefined || !editor) { return }
    editor.commands.focus('end', { scrollIntoView: false })
  }, [focusKey, editor])

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 rounded-md">
      <EditorContent editor={editor} />
    </div>
  )
}

export default TipTapPrivatePost

TipTapPrivatePost.propTypes = {
  html: PropTypes.string,
  updateHtml: PropTypes.func.isRequired,
  idx: PropTypes.number,
  onEnter: PropTypes.func,
  focusKey: PropTypes.any,
  onCaret: PropTypes.func,
  onKeyDown: PropTypes.func,
  onEditor: PropTypes.func,
  placeholder: PropTypes.string
}
